import { UndoOutlined } from '@ant-design/icons';
import { Button, Grid, Input, Modal, Select, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationsFilter } from '../../../../shared/VolunteerApplicationServiceTypes';
import {
	VALID_ABHYASI_ID_REGEX,
	Gender as GENDER,
} from '../../../../shared/Constants';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface FilterProposerReassignProps {
	open: boolean;
	value: ApplicationsFilter;
	onClose: () => void;
	onFilterChange: (value: ApplicationsFilter) => void;
	resetFilterValues: boolean;
	setResetFilterValues: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FilterProposerReassign = ({
	open,
	onClose,
	value: { searchByProposerSrcmId, searchByProposerName, gender },
	onFilterChange,
	resetFilterValues,
	setResetFilterValues,
}: FilterProposerReassignProps) => {
	const { t, i18n } = useTranslation();
	const [proposerNameValue, setProposerNameValue] = useState<
		string | undefined
	>(searchByProposerName);
	const [proposerSrcmIdError, setProposerSrcmIdError] = useState('');
	const [proposerSrcmId, setProposerSrcmId] = useState<string | undefined>(
		searchByProposerSrcmId
	);
	const [genderValue, setGenderValue] = useState<string | undefined>(gender);
	const lengthyLanguages = ['ta', 'te', 'ka', 'ma'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;

	const onProposerSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value;
			setProposerSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setProposerSrcmIdError('invalidAbhyasiId');
			} else {
				setProposerSrcmIdError('');
			}
		},
		[]
	);

	const onNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setProposerNameValue(e.target.value);
		},
		[]
	);

	const onGenderChange = useCallback((value: string) => {
		setGenderValue(value);
	}, []);

	const fireFilterChange = useCallback(() => {
		if (genderValue) {
			onFilterChange({
				searchByProposerSrcmId: proposerSrcmId,
				searchByProposerName: proposerNameValue,
				gender: genderValue,
			});
		} else {
			onFilterChange({
				searchByProposerSrcmId: proposerSrcmId,
				searchByProposerName: proposerNameValue,
			});
		}
	}, [onFilterChange, proposerSrcmId, proposerNameValue, genderValue]);

	const handleFilterButtonClick = useCallback(() => {
		fireFilterChange();
		onClose();
	}, [fireFilterChange, onClose]);

	const handleResetFilters = useCallback(() => {
		setProposerSrcmId('');
		setProposerNameValue('');
		setGenderValue('');
		onFilterChange({});
		onClose();
	}, [onClose, onFilterChange]);

	useEffect(() => {
		setProposerSrcmId('');
		setProposerNameValue('');
		setGenderValue('');
		setResetFilterValues(false);
	}, [resetFilterValues, setResetFilterValues]);

	return (
		<div className="flex flex-col h-full min-h-0 p-[8px]">
			<Modal
				open={open}
				onCancel={onClose}
				footer={null}
				centered
				className={
					isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
				}
				closable={true}
				maskClosable={true}
			>
				<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
					{t('filter')}
				</div>
				<div className="flex flex-col md:flex-row gap-4">
					<div className="mb-[8px] flex flex-col gap-2 w-full md:w-1/2">
						<Text className="block text-xs text-gray-650 pb-[8px]">
							{t('proposerName')}
						</Text>
						<Input
							className="flex min-w-[200px]"
							value={proposerNameValue}
							allowClear
							onChange={onNameChange}
						/>
					</div>

					<div className="mb-[8px] flex flex-col gap-2 w-full md:w-1/2">
						<Text className="block text-xs text-gray-650 pb-[8px]">
							{t('proposerSRCMID')}
						</Text>
						<Input
							className="flex min-w-[200px]"
							value={proposerSrcmId}
							allowClear
							onChange={onProposerSRCMIdChange}
						/>
						{proposerSrcmIdError && (
							<Text className="text-xs text-red-500">
								{t(proposerSrcmIdError)}
							</Text>
						)}
					</div>
				</div>
				<div className="flex flex-col md:flex-row gap-4">
					<div className="mb-[8px] flex flex-col gap-2 w-full md:w-1/2">
						<Text className="block text-xs text-gray-650 pb-[8px]">
							{t('gender')}
						</Text>
						<Select
							showSearch
							defaultActiveFirstOption={false}
							showArrow={false}
							filterOption={false}
							placeholder={t('gender')}
							options={[
								{
									value: GENDER.MALE,
									label: t('male'),
								},
								{
									value: GENDER.FEMALE,
									label: t('female'),
								},
							]}
							onChange={onGenderChange}
							value={genderValue}
						></Select>
					</div>
				</div>
				<div className="flex flex-col items-center space-y-2 gap-3 md:flex-row md:justify-center md:gap-2 mt-4 ant-modal-footer">
					<Button
						className="w-full md:flex-1 h-9 text-primary-blue font-sans font-bold"
						type="link"
						onClick={handleResetFilters}
					>
						<span>
							{t('resetFilters')}
							<UndoOutlined className="ml-2" />
						</span>
					</Button>
					<Button
						className={`w-full md:flex-1 h-9 text-primary-blue font-sans font-bold ${
							isStackVertical ? 'p-2' : ''
						}`}
						onClick={onClose}
					>
						{t('closeFilter')}
					</Button>
					<Button
						type="primary"
						onClick={handleFilterButtonClick}
						className={`w-full md:flex-1 h-9 font-sans font-bold ${
							isStackVertical ? 'p-2 mt-3' : ''
						}`}
					>
						{t('applyFilter')}
					</Button>
				</div>
			</Modal>
		</div>
	);
};
